import React, { useEffect, useMemo } from "react";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";

import { dateInFuture } from "shared/lib/functions";
import { isFalsy } from "shared/lib/isFalsy";
import { formattedMessageParams } from "shared/messages";

import DateInput from "@components/Forms/Inputs/DateInput";
import { checkRequired } from "@components/Forms/Rules";
import { Field, Select } from "@components/Profile/Forms/HooksForm";

const {
  passportNumberText,
  passportNumberPlaceholder,
  expirationDate,
  citizenshipText,
  citizenshipPlaceholder,
  issuedText,
  issuedPlaceholder,
} = defineMessages({
  passportNumberText: formattedMessageParams("Form.passportNumber"),
  passportNumberPlaceholder: formattedMessageParams(
    "Profile.passportNumberPlaceholder"
  ),
  expirationDate: formattedMessageParams("Form.passportValidity"),
  citizenshipText: formattedMessageParams("Form.citizenship"),
  citizenshipPlaceholder: formattedMessageParams(
    "Profile.citizenshipPlaceholder"
  ),
  issuedText: formattedMessageParams("Form.passportIssued"),
  issuedPlaceholder: formattedMessageParams("Profile.issuedPlaceholder"),
});

export default function PassportdentityDocumentsForm({
  setFormVals,
  formVals,
  index,
  errors,
  setErrors,
  defaultCountry,
  countries,
}) {
  const intl = useIntl();

  useEffect(() => {
    if (countries.length > 0 && !!defaultCountry) {
      const foundCountryByAgency = countries.find(
        (country) => country.Code === defaultCountry
      );
      if (foundCountryByAgency) {
        if (isFalsy(formVals?.passports[index].Issued)) {
          setValueInForm(foundCountryByAgency.Code, "Issued");
        }
        if (isFalsy(formVals?.passports[index].Citizenship)) {
          setValueInForm(foundCountryByAgency.Code, "Citizenship");
        }
        setErrors((state) => ({ ...state, Citizenship: null, Issued: null }));
      }
    }
    // eslint-disable-next-line
  }, [countries]);

  const countriesOptions = useMemo(
    () =>
      countries.map(({ Code, $t }) => ({
        value: Code,
        label: $t,
      })),
    [countries]
  );

  const setValueInForm = async (newVal, key) => {
    await setFormVals((prevState) => {
      const updatedPassports = prevState.passports.map((pp, idx) =>
        idx === index ? { ...pp, [key]: newVal } : pp
      );
      return {
        ...prevState,
        passports: updatedPassports,
      };
    });
  };

  return (
    <>
      <Field
        required
        name="Number"
        id="passportInfo-number"
        label={intl.formatMessage(passportNumberText)}
        placeholder={intl.formatMessage(passportNumberPlaceholder)}
        value={formVals?.passports[index].Number}
        error={errors.Number}
        setter={(newVal) => {
          const err = checkRequired(newVal);
          setErrors((state) => ({ ...state, Number: err }));
          setValueInForm(newVal, "Number");
        }}
      />
      <Field
        required
        name="ValidityDate"
        id="passportInfo-validityDate"
        component={DateInput}
        yearsFromNow
        value={formVals?.passports[index].ValidityDate}
        error={errors.ValidityDate}
        setter={(newVal) => {
          const [day, month, year] = newVal?.split("-")?.reverse() || [];
          const err = (!newVal?.trim() || !dateInFuture(day, month, year)) && (
            <FormattedMessage id="Profile.wrongFilledDate" />
          );
          setErrors((state) => ({ ...state, ValidityDate: err }));
          setValueInForm(newVal, "ValidityDate");
        }}
        label={intl.formatMessage(expirationDate)}
      />
      <Field
        required
        name="Issued"
        id="passportInfo-passportIssued"
        component={Select}
        options={countriesOptions}
        label={intl.formatMessage(issuedText)}
        placeholder={intl.formatMessage(issuedPlaceholder)}
        value={formVals?.passports[index].Issued}
        error={errors.Issued}
        setter={(newVal) => {
          const err = checkRequired(newVal);
          setErrors((state) => ({ ...state, Issued: err }));
          setValueInForm(newVal, "Issued");
        }}
      />
      <Field
        required
        name="Citizenship"
        id="passportInfo-passportCitizenship"
        label={intl.formatMessage(citizenshipText)}
        component={Select}
        options={countriesOptions}
        placeholder={intl.formatMessage(citizenshipPlaceholder)}
        value={formVals?.passports[index].Citizenship}
        error={errors.Citizenship}
        setter={(newVal) => {
          const err = checkRequired(newVal);
          setErrors((state) => ({ ...state, Citizenship: err }));
          setValueInForm(newVal, "Citizenship");
        }}
      />
    </>
  );
}
